import type { ReactNode } from 'react';
import { PackageIcon, SwapIcon, XIcon } from '@phosphor-icons/react';
import { AssociateProductV2 } from '@/components/products/AssociateProductV2';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { T, Var } from 'gt-react';
import {
  allowDialogClose,
  showSavedProduct,
  type MappingMode,
} from './mappingForm';
import { useMappingFormState } from './useMappingFormState';
import type { MappingValidationError } from './mappingForm';

export interface MappingCopy {
  editTitle: ReactNode;
  createTitle: ReactNode;
  editDescription: ReactNode;
  createDescription: ReactNode;
  partyLabel: ReactNode;
  termsLabel: ReactNode;
  termsHelp: ReactNode;
  termPlaceholder: string;
  emptyTerms: ReactNode;
  deleteTitle: ReactNode;
  deleteBody: ReactNode;
  moveDescription: ReactNode;
}

interface ProductSummary {
  productName: string;
  variantName: string | null;
  variantSku: string | null;
}

interface MappingModalProps {
  open: boolean;
  mode: MappingMode;
  defaultPartyId?: string;
  partyName?: string;
  initialProductId?: string;
  initialVariantId?: string;
  initialTerms?: string[] | null;
  productSummary?: ProductSummary;
  onOpenChange: (open: boolean) => void;
  onInvalid: (error: MappingValidationError) => void;
  onCreate: (input: {
    partyId: string;
    productVariantId: string;
    terms: string[];
  }) => void;
  onUpdate: (input: { terms: string[]; productVariantId?: string }) => void;
  onDelete: () => void;
  isSaving: boolean;
  isDeleting: boolean;
  renderPartyPicker: (
    partyId: string | undefined,
    onSelect: (id: string | undefined) => void,
  ) => ReactNode;
  copy: MappingCopy;
}

function ProductSummaryCard({
  summary,
  onChange,
}: {
  summary: ProductSummary;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
          <PackageIcon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{summary.productName}</p>
          <VariantMeta summary={summary} />
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onChange}
        className="shrink-0 ml-2"
      >
        <SwapIcon className="h-4 w-4 mr-1.5" />
        <T context="button to change mapped product">Change</T>
      </Button>
    </div>
  );
}

function VariantMeta({ summary }: { summary: ProductSummary }) {
  return (
    <div className="flex items-center gap-2">
      {summary.variantName ? (
        <span className="text-xs text-muted-foreground truncate">
          {summary.variantName}
        </span>
      ) : null}
      {summary.variantSku ? (
        <Badge variant="outline" className="text-xs shrink-0">
          <T context="product variant SKU label">
            SKU: <Var>{summary.variantSku}</Var>
          </T>
        </Badge>
      ) : null}
    </div>
  );
}

function ProductPicker({
  isEdit,
  changingProduct,
  productId,
  productVariantId,
  onSelect,
  onCancel,
}: {
  isEdit: boolean;
  changingProduct: boolean;
  productId?: string;
  productVariantId?: string;
  onSelect: (productId: string, productVariantId: string) => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-2">
      <AssociateProductV2
        selectedProductId={productId}
        selectedProductVariantId={productVariantId}
        onVariantSelected={({
          productId: selectedProductId,
          productVariantId: selectedVariantId,
        }) => {
          if (!selectedProductId || !selectedVariantId) return;
          onSelect(selectedProductId, selectedVariantId);
        }}
      />
      {isEdit && changingProduct ? (
        <ChangeProductWarning onCancel={onCancel} />
      ) : null}
    </div>
  );
}

function ChangeProductWarning({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <p className="text-xs text-muted-foreground flex-1">
        <T context="warning when changing mapped product">
          Changing the product will move this mapping to the new variant.
        </T>
      </p>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        <T context="cancel changing product">Cancel</T>
      </Button>
    </div>
  );
}

function TermsEditor({
  terms,
  placeholder,
  emptyTerms,
  onChange,
  onRemove,
}: {
  terms: string[];
  placeholder: string;
  emptyTerms: ReactNode;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="space-y-2">
      {terms.map((term, index) => (
        <TermRow
          key={`term-${index}`}
          term={term}
          placeholder={placeholder}
          onChange={(value) => onChange(index, value)}
          onRemove={() => onRemove(index)}
        />
      ))}
      {terms.length === 0 ? (
        <div className="text-sm text-muted-foreground">{emptyTerms}</div>
      ) : null}
    </div>
  );
}

function TermRow({
  term,
  placeholder,
  onChange,
  onRemove,
}: {
  term: string;
  placeholder: string;
  onChange: (value: string) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input
        value={term}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="shrink-0"
      >
        <XIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

function ConfirmAlert({
  open,
  onOpenChange,
  title,
  description,
  pending,
  pendingLabel,
  confirmLabel,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  pending: boolean;
  pendingLabel: ReactNode;
  confirmLabel: ReactNode;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            <T context="cancel button">Cancel</T>
          </AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={pending}>
            {pending ? pendingLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MappingFooter({
  isEdit,
  isSaving,
  isDeleting,
  onDelete,
  onCancel,
  onSave,
}: {
  isEdit: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <DialogFooter>
      {isEdit ? (
        <Button
          type="button"
          variant="destructive"
          onClick={onDelete}
          disabled={isDeleting}
        >
          <T context="delete customer product mapping">Delete mapping</T>
        </Button>
      ) : null}
      <Button type="button" variant="outline" onClick={onCancel}>
        <T context="cancel dialog">Cancel</T>
      </Button>
      <Button type="button" onClick={onSave} disabled={isSaving}>
        {isSaving ? (
          <T context="save in progress">Saving...</T>
        ) : (
          <T context="save mapping">Save</T>
        )}
      </Button>
    </DialogFooter>
  );
}

function handleDialogOpenChange(
  isOpen: boolean,
  deleteOpen: boolean,
  moveOpen: boolean,
  onOpenChange: (open: boolean) => void,
) {
  if (!allowDialogClose(isOpen, deleteOpen || moveOpen)) return;
  onOpenChange(isOpen);
}

function MappingHeader({
  isEdit,
  copy,
}: {
  isEdit: boolean;
  copy: MappingCopy;
}) {
  const title = isEdit ? copy.editTitle : copy.createTitle;
  const description = isEdit ? copy.editDescription : copy.createDescription;
  return (
    <DialogHeader>
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{description}</DialogDescription>
    </DialogHeader>
  );
}

function ProductLabel() {
  return (
    <Label>
      <T context="form label">Product</T>
    </Label>
  );
}

function ProductField({
  isEdit,
  changingProduct,
  productSummary,
  productId,
  productVariantId,
  onChangeProduct,
  onSelect,
  onCancel,
}: {
  isEdit: boolean;
  changingProduct: boolean;
  productSummary?: ProductSummary;
  productId?: string;
  productVariantId?: string;
  onChangeProduct: () => void;
  onSelect: (productId: string, productVariantId: string) => void;
  onCancel: () => void;
}) {
  if (
    showSavedProduct(isEdit, changingProduct, productSummary !== undefined) &&
    productSummary
  ) {
    return (
      <div className="space-y-3">
        <ProductLabel />
        <ProductSummaryCard
          summary={productSummary}
          onChange={onChangeProduct}
        />
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <ProductLabel />
      <ProductPicker
        isEdit={isEdit}
        changingProduct={changingProduct}
        productId={productId}
        productVariantId={productVariantId}
        onSelect={onSelect}
        onCancel={onCancel}
      />
    </div>
  );
}

function PartyField({
  label,
  partyName,
  partyId,
  onSelect,
  renderPartyPicker,
}: {
  label: ReactNode;
  partyName?: string;
  partyId?: string;
  onSelect: (id: string | undefined) => void;
  renderPartyPicker: MappingModalProps['renderPartyPicker'];
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {partyName ? (
        <p className="text-sm font-medium">{partyName}</p>
      ) : (
        renderPartyPicker(partyId, onSelect)
      )}
    </div>
  );
}

function TermsField({
  copy,
  terms,
  onAdd,
  onChange,
  onRemove,
}: {
  copy: MappingCopy;
  terms: string[];
  onAdd: () => void;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>{copy.termsLabel}</Label>
        <Button variant="outline" size="sm" onClick={onAdd}>
          <T context="add customer term row">Add term</T>
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{copy.termsHelp}</p>
      <TermsEditor
        terms={terms}
        placeholder={copy.termPlaceholder}
        emptyTerms={copy.emptyTerms}
        onChange={onChange}
        onRemove={onRemove}
      />
    </div>
  );
}

function MappingFields({
  isEdit,
  form,
  partyName,
  productSummary,
  renderPartyPicker,
  copy,
}: {
  isEdit: boolean;
  form: ReturnType<typeof useMappingFormState>;
  partyName?: string;
  productSummary?: ProductSummary;
  renderPartyPicker: MappingModalProps['renderPartyPicker'];
  copy: MappingCopy;
}) {
  const lockedPartyName = isEdit ? partyName : undefined;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-2 -mr-2">
      <div className="space-y-6">
        <ProductField
          isEdit={isEdit}
          changingProduct={form.changingProduct}
          productSummary={productSummary}
          productId={form.productId}
          productVariantId={form.productVariantId}
          onChangeProduct={() => form.setChangingProduct(true)}
          onSelect={form.setProduct}
          onCancel={form.cancelProductChange}
        />
        <Separator />
        <PartyField
          label={copy.partyLabel}
          partyName={lockedPartyName}
          partyId={form.partyId}
          onSelect={form.setPartyId}
          renderPartyPicker={renderPartyPicker}
        />
        <Separator />
        <TermsField
          copy={copy}
          terms={form.terms}
          onAdd={form.addTerm}
          onChange={form.changeTerm}
          onRemove={form.removeTermAt}
        />
      </div>
    </div>
  );
}

export function ProductMatchMappingModal({
  open,
  mode,
  defaultPartyId,
  partyName,
  initialProductId,
  initialVariantId,
  initialTerms,
  productSummary,
  onOpenChange,
  onInvalid,
  onCreate,
  onUpdate,
  onDelete,
  isSaving,
  isDeleting,
  renderPartyPicker,
  copy,
}: MappingModalProps) {
  const isEdit = mode === 'edit';
  const form = useMappingFormState({
    mode,
    initialPartyId: defaultPartyId,
    initialProductId,
    initialVariantId,
    initialTerms,
    savedVariantId: initialVariantId,
    onInvalid,
    onCreate,
    onUpdate,
  });
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(isOpen) =>
          handleDialogOpenChange(
            isOpen,
            form.deleteConfirmOpen,
            form.moveConfirmOpen,
            onOpenChange,
          )
        }
      >
        <DialogContent className="w-[min(96vw,72rem)] max-w-none sm:max-w-none max-h-[90vh] flex flex-col overflow-hidden">
          <MappingHeader isEdit={isEdit} copy={copy} />
          <MappingFields
            isEdit={isEdit}
            form={form}
            partyName={partyName}
            productSummary={productSummary}
            renderPartyPicker={renderPartyPicker}
            copy={copy}
          />
          <MappingFooter
            isEdit={isEdit}
            isSaving={isSaving}
            isDeleting={isDeleting}
            onDelete={() => form.setDeleteConfirmOpen(true)}
            onCancel={() => onOpenChange(false)}
            onSave={form.save}
          />
        </DialogContent>
      </Dialog>
      <ConfirmAlert
        open={form.deleteConfirmOpen}
        onOpenChange={form.setDeleteConfirmOpen}
        title={copy.deleteTitle}
        description={copy.deleteBody}
        pending={isDeleting}
        pendingLabel={<T context="delete in progress">Deleting...</T>}
        confirmLabel={<T context="confirm delete">Delete</T>}
        onConfirm={onDelete}
      />
      <ConfirmAlert
        open={form.moveConfirmOpen}
        onOpenChange={form.setMoveConfirmOpen}
        title={
          <T context="confirm move mapping to new product">
            Move mapping to new product?
          </T>
        }
        description={copy.moveDescription}
        pending={false}
        pendingLabel={null}
        confirmLabel={<T context="confirm move mapping">Move mapping</T>}
        onConfirm={form.confirmMove}
      />
    </>
  );
}
